import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Archive, Ban, Lock, Trash2, X } from 'lucide-react';
import {
  archiveInventoryItem,
  bulkArchiveInventory,
  bulkDeleteInventory,
  bulkDiscontinueInventory,
  deleteInventoryItem,
  getInventoryItemReferences,
  type BulkInventoryResult,
  type InventoryItemListRow,
  type InventoryItemReferences,
} from '@/lib/api/inventory.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { cn } from '@/lib/utils.js';

type ItemLite = { id: string; name: string; qtyOnHand?: string | number };

function ModalShell({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className={cn(
          'max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white shadow-xl',
          wide ? 'max-w-lg' : 'max-w-md',
        )}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function BlockersList({ blockers }: { blockers: InventoryItemReferences['archiveBlockers'] }) {
  return (
    <ul className="max-h-40 space-y-1 overflow-auto rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
      {blockers.map((b, i) => (
        <li key={`${b.kind}-${b.id ?? i}`}>
          {b.href ? (
            <a href={b.href} className="text-primary underline">
              {b.label}
            </a>
          ) : (
            b.label
          )}
        </li>
      ))}
    </ul>
  );
}

function formatByTable(byTable: Record<string, number> | undefined): string[] {
  if (!byTable) return [];
  return Object.entries(byTable)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([table, n]) => `${n} ${table.replace(/_/g, ' ')}`);
}

export function ArchiveInventoryModal({
  open,
  item,
  onClose,
  onDone,
}: {
  open: boolean;
  item: ItemLite | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [stockMode, setStockMode] = useState<'keep' | 'writeoff'>('keep');

  const refsQuery = useQuery({
    queryKey: ['inventory-item-refs', item?.id],
    queryFn: () => getInventoryItemReferences(item!.id),
    enabled: open && Boolean(item?.id),
  });

  useEffect(() => {
    if (open) {
      setReason('');
      setStockMode('keep');
    }
  }, [open, item?.id]);

  const mut = useMutation({
    mutationFn: () =>
      archiveInventoryItem(item!.id, {
        reason: reason.trim() || null,
        acknowledgeStock: stockMode === 'keep',
        writeOffStock: stockMode === 'writeoff',
      }),
    onSuccess: () => {
      toastSuccess('Item archived');
      onDone();
      onClose();
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  if (!open || !item) return null;

  const refs = refsQuery.data;
  const hardBlockers = (refs?.archiveBlockers ?? []).filter((b) => b.kind !== 'stock');
  const blocked = hardBlockers.length > 0;
  const qty = refs?.qtyOnHand ?? Number(item.qtyOnHand ?? 0);

  return (
    <ModalShell title={blocked ? `Cannot archive "${item.name}"` : `Archive "${item.name}"?`} onClose={onClose}>
      {refsQuery.isLoading ? (
        <p className="text-sm text-slate-500">Checking references…</p>
      ) : blocked ? (
        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            Resolve these before archiving:
          </p>
          <BlockersList blockers={hardBlockers} />
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {qty > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              <p className="font-medium">⚠ {qty} units currently in stock</p>
              <div className="mt-2 space-y-1.5">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="stockMode"
                    checked={stockMode === 'keep'}
                    onChange={() => setStockMode('keep')}
                  />
                  Keep stock as-is (hidden but quantity preserved)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="stockMode"
                    checked={stockMode === 'writeoff'}
                    onChange={() => setStockMode('writeoff')}
                  />
                  Write off stock (adjustment, books a loss)
                </label>
              </div>
            </div>
          ) : null}
          <label className="block text-sm">
            <span className="text-slate-600">Reason (optional)</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Discontinued by supplier"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={mut.isPending}
              onClick={() => mut.mutate()}
            >
              <Archive className="h-4 w-4" />
              Archive
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

export function DeleteInventoryModal({
  open,
  item,
  onClose,
  onDone,
  onArchiveInstead,
}: {
  open: boolean;
  item: ItemLite | null;
  onClose: () => void;
  onDone: () => void;
  onArchiveInstead?: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  const refsQuery = useQuery({
    queryKey: ['inventory-item-refs', item?.id],
    queryFn: () => getInventoryItemReferences(item!.id),
    enabled: open && Boolean(item?.id),
  });

  useEffect(() => {
    if (open) {
      setReason('');
      setConfirmName('');
    }
  }, [open, item?.id]);

  const mut = useMutation({
    mutationFn: (managerOverrideAuthorizedBy: string) =>
      deleteInventoryItem(item!.id, {
        reason: reason.trim(),
        confirmName: confirmName.trim(),
        managerOverrideAuthorizedBy,
      }),
    onSuccess: () => {
      toastSuccess('Item permanently deleted');
      onDone();
      onClose();
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  if (!open || !item) return null;

  const refs = refsQuery.data;
  const blocked = refs != null && !refs.canHardDelete;
  const nameOk = confirmName.trim().toLowerCase() === item.name.trim().toLowerCase();
  const reasonOk = reason.trim().length >= 3;

  return (
    <>
      <ModalShell
        title={blocked ? `Cannot delete "${item.name}"` : `Permanently delete "${item.name}"?`}
        onClose={onClose}
      >
        {refsQuery.isLoading ? (
          <p className="text-sm text-slate-500">Checking references…</p>
        ) : blocked ? (
          <div className="space-y-3">
            <p className="flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Referenced by:
            </p>
            <ul className="space-y-1 text-sm text-slate-700">
              {formatByTable(refs!.byTable).map((line) => (
                <li key={line}>• {line}</li>
              ))}
            </ul>
            <p className="text-sm text-slate-600">
              Deleting would corrupt COGS and P&amp;L history. Archive instead.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                onClick={onClose}
              >
                Cancel
              </button>
              {onArchiveInstead ? (
                <button
                  type="button"
                  className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white"
                  onClick={onArchiveInstead}
                >
                  Archive Instead
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              No usage history found. Cannot be undone.
            </p>
            <label className="block text-sm">
              <span className="text-slate-600">Type the item name to confirm</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Reason</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Test SKU from import"
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!nameOk || !reasonOk || mut.isPending}
                onClick={async () => {
                  const result = await requireOverride({
                    action: 'delete_inventory_item',
                    title: 'Manager Approval to Delete Item',
                    description: `Permanently deleting ${item.name}`,
                    requiredRole: 'tenant_admin',
                  });
                  if (!result.approved) return;
                  mut.mutate(result.authorizedBy.id);
                }}
              >
                <Lock className="h-4 w-4" />
                Delete permanently
              </button>
            </div>
          </div>
        )}
      </ModalShell>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </>
  );
}

export function BulkArchiveInventoryModal({
  open,
  itemIds,
  onClose,
  onDone,
}: {
  open: boolean;
  itemIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [preview, setPreview] = useState<BulkInventoryResult | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setPreview(null);
    setLoading(true);
    void bulkArchiveInventory({ itemIds, dryRun: true })
      .then(setPreview)
      .catch((e) => toastError(parseApiError(e).message))
      .finally(() => setLoading(false));
  }, [open, itemIds]);

  const mut = useMutation({
    mutationFn: () =>
      bulkArchiveInventory({
        itemIds,
        reason: reason.trim() || null,
        acknowledgeStock: true,
        dryRun: false,
      }),
    onSuccess: (res) => {
      toastSuccess(`Archived ${res.succeeded.length} items`);
      onDone();
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (!open) return null;

  const will = preview?.succeeded.length ?? 0;
  const skipped = preview?.skipped ?? [];

  return (
    <ModalShell title={`Archive ${itemIds.length} items?`} onClose={onClose} wide>
      {loading || !preview ? (
        <p className="text-sm text-slate-500">Previewing…</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            ✓ {will} will be archived
            {skipped.length ? (
              <>
                <br />⚠ {skipped.length} skipped
              </>
            ) : null}
          </p>
          {skipped.length > 0 ? (
            <ul className="max-h-36 space-y-1 overflow-auto rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
              {skipped.map((s) => (
                <li key={s.id}>
                  {s.name ?? s.id.slice(0, 8)} — {s.reason.replace(/_/g, ' ')}
                  {s.qtyOnHand != null ? ` (stock ${s.qtyOnHand})` : ''}
                </li>
              ))}
            </ul>
          ) : null}
          <label className="block text-sm">
            <span className="text-slate-600">Reason (optional)</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <p className="text-xs text-slate-500">
            Items with stock will keep quantity (acknowledge default for bulk).
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={will === 0 || mut.isPending}
              className="rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => mut.mutate()}
            >
              Archive {will} Items
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

export function BulkDiscontinueInventoryModal({
  open,
  itemIds,
  onClose,
  onDone,
}: {
  open: boolean;
  itemIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const mut = useMutation({
    mutationFn: () => bulkDiscontinueInventory({ itemIds, dryRun: false }),
    onSuccess: (res) => {
      toastSuccess(`Discontinued ${res.succeeded.length}; skipped ${res.skipped.length}`);
      onDone();
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (!open) return null;

  return (
    <ModalShell title={`Discontinue ${itemIds.length} items?`} onClose={onClose}>
      <p className="text-sm text-slate-600">
        They stay visible in inventory lists and reports but cannot be added to new tickets or POs.
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
        >
          <Ban className="h-4 w-4" />
          Discontinue
        </button>
      </div>
    </ModalShell>
  );
}

export function BulkDeleteInventoryModal({
  open,
  itemIds,
  onClose,
  onDone,
}: {
  open: boolean;
  itemIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [preview, setPreview] = useState<BulkInventoryResult | null>(null);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  useEffect(() => {
    if (!open) return;
    setReason('');
    setConfirmText('');
    setPreview(null);
    setLoading(true);
    void bulkDeleteInventory({
      itemIds,
      reason: 'preview',
      confirmText: 'DELETE',
      dryRun: true,
    })
      .then(setPreview)
      .catch((e) => toastError(parseApiError(e).message))
      .finally(() => setLoading(false));
  }, [open, itemIds]);

  const mut = useMutation({
    mutationFn: (managerOverrideAuthorizedBy: string) =>
      bulkDeleteInventory({
        itemIds,
        reason: reason.trim(),
        confirmText: 'DELETE',
        managerOverrideAuthorizedBy,
        dryRun: false,
      }),
    onSuccess: (res) => {
      toastSuccess(`Deleted ${res.succeeded.length}; skipped ${res.skipped.length}`);
      onDone();
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (!open) return null;

  const will = preview?.succeeded.length ?? 0;

  return (
    <>
      <ModalShell title={`Permanently delete ${itemIds.length} items?`} onClose={onClose} wide>
        {loading || !preview ? (
          <p className="text-sm text-slate-500">Previewing…</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">
              ✓ {will} deletable
              {preview.skipped.length ? (
                <>
                  <br />⚠ {preview.skipped.length} blocked by references
                </>
              ) : null}
            </p>
            {preview.skipped.length > 0 ? (
              <ul className="max-h-36 space-y-1 overflow-auto rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                {preview.skipped.map((s) => (
                  <li key={s.id}>
                    {s.name ?? s.id.slice(0, 8)} — has references
                  </li>
                ))}
              </ul>
            ) : null}
            <label className="block text-sm">
              <span className="text-slate-600">Reason</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Type DELETE to confirm</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  will === 0 ||
                  reason.trim().length < 3 ||
                  confirmText !== 'DELETE' ||
                  mut.isPending
                }
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                onClick={async () => {
                  const result = await requireOverride({
                    action: 'bulk_delete_inventory',
                    title: 'Manager Approval to Delete Items',
                    description: `Permanently deleting ${will} item(s)`,
                    requiredRole: 'tenant_admin',
                  });
                  if (!result.approved) return;
                  mut.mutate(result.authorizedBy.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete {will}
              </button>
            </div>
          </div>
        )}
      </ModalShell>
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </>
  );
}

export type { InventoryItemListRow };
